import React, { useState, useRef, useEffect, useMemo } from "react";
import { useClickAway } from "react-use";
import Immutable from "immutable";
import { useVirtualizer } from "@tanstack/react-virtual";
import _ from "lodash";
import Fuse from "fuse.js";

import { Box, Select, SelectOption, Text, Button, Icon, Checkbox } from "@showitinc/showit-wc";
import AppConstants from "../constants/AppConstants";
import useGoogleFonts from "../hooks/query/useGoogleFonts";
import When from "./utils/When";
import uiPrefix from "../utils/UiPrefixUtils";
import styles from "./FontPanelGoogle.module.css";
import sharedStyles from "./DesignSettings/DesignSettingsFont.module.css";
import GoogleFontPreview from "@/app2components/base/GoogleFontPreview";
import FontPreview from "./FontPreview";

const { GOOGLE_FONT_VARIANTS } = AppConstants;

/**
 * @typedef {Object} GoogleFont
 * @prop {string} category - serif, sans-serif etc
 * @prop {string} family - - the name of the font family ex. "Playfair Display"
 * @prop {Map<string, string>} files - font file urls keyed by variant
 * @prop {string} kind - "webfonts#webfont" 🤷‍♂️ aren't they all?
 * @prop {string} lastModified - YYYY-MM-DD
 * @prop {string} menu - ttf link, probably
 * @prop {string[]} subsets - latin, latin-ext etc
 * @prop {string[]} variants - string representing font weight 100-900, "regular", "italic"
 * @prop {string} version
 */

/**
 * A font configuration format stored in a design's theme
 * @typedef {Object} ThemeFont
 * @prop {"google"|"custom"} type - font type
 * @prop {string} family - the name of the font family ex. "Playfair Display"
 * @prop {string} name - the name of the font family and the string representation of a font variant ex. "Playfair Display Bold Italic"
 * @prop {string|number} weight - string/number representing font weight 100-900, "regular", "italic"
 * @prop {"italic"=} style - optional param set if the font variant is italic
 * @prop {string=} src - source url for the font if type is set to custom
 */

const defaultFont = { weight: "regular", type: "google" };

const _getFontDisplayName = (fontName, weight, style) => {
  let newFontName = fontName;
  if (weight && weight !== "") {
    newFontName += ` ${GOOGLE_FONT_VARIANTS[weight].display}`;
  }
  if (style && style === "italic") {
    newFontName += " Italic";
  }
  return newFontName;
};

const FontPanelGoogle = ({ addFont, fonts: themeFonts, designContext }) => {
  const searchListRef = useRef(null);

  const [font, setFont] = useState({ ...defaultFont });
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("alpha");
  const [category, setCategory] = useState("all");
  const [weights, setWeights] = useState([]);
  const [searchingFonts, setSearchingFonts] = useState(false);
  const [selectedWeights, setSelectedWeights] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [styleHolder, setStyleHolder] = useState(null);

  const {
    isLoading,
    isError,
    isSuccess,
    data: googleFontList = [],
  } = useGoogleFonts({ enabled: true, sortBy, category });

  const commandSearchIndex = useMemo(
    () =>
      new Fuse(googleFontList, {
        includeScore: true,
        threshold: 0.4,
        keys: ["family"],
      }),
    [googleFontList]
  );

  const filteredFonts = useMemo(() => {
    if (!searchQuery) return { refIndex: 0, item: googleFontList[0] };
    return commandSearchIndex?.search(searchQuery);
  }, [googleFontList, searchQuery]);

  useClickAway(searchListRef, () => {
    setSearchingFonts(false);
  });

  const rowVirtualizer = useVirtualizer({
    count: googleFontList.length,
    getScrollElement: () => searchListRef.current,
    estimateSize: () => 24,
    overscan: 5,
  });

  useEffect(() => {
    if (searchQuery) {
      rowVirtualizer.scrollToIndex(filteredFonts[0]?.refIndex || 0, { align: "start" });
    }
  }, [filteredFonts, searchQuery, rowVirtualizer]);

  useEffect(() => {
    const tempStyleHolder = document.createElement("div");
    tempStyleHolder.style.display = "none";
    tempStyleHolder.id = "google-font-style-holder";
    document.head.appendChild(tempStyleHolder);
    setStyleHolder(tempStyleHolder);

    return () => {
      document.head.removeChild(tempStyleHolder);
    };
  }, []);

  const resetPanel = () => {
    setFont({ ...defaultFont });
    setWeights([]);
    setSearchQuery("");
    setSearchingFonts(false);
  };

  const _getNextAvailableVariant = (family, variants) => {
    if (!isWeightInTheme(family, GOOGLE_FONT_VARIANTS.regular.display)) return "regular";
    const availableVariant = variants.find(
      (variant) => !isWeightInTheme(family, GOOGLE_FONT_VARIANTS[variant].display)
    );
    return availableVariant || "regular";
  };

  const _onFamilyChange = (family, selectedFont) => {
    if (family) {
      const { variants = [] } = selectedFont;
      setSelectedFamily(selectedFont);

      const availableVariant = _getNextAvailableVariant(family, variants);
      setSelectedWeights([availableVariant]);

      const newFamily = {
        ...font,
        family,
        weight: availableVariant,
        name: _getFontDisplayName(family, availableVariant),
      };

      setWeights(variants);
      setFont({ ...newFamily });
      setSearchQuery(family);
      setSearchingFonts(false);
      return;
    }
    resetPanel();
  };

  const _onSortByChange = (e) => {
    setSearchQuery("");
    setSortBy(e.target.value);
    setFont({ ...defaultFont });
  };

  const _onCategoryChange = (e) => {
    setSearchQuery("");
    setCategory(e.target.value);
    setFont({ ...defaultFont });
  };

  /**
   * Searches through provided font map and returns true if the font name and variant exists
   * @param {string} fontName
   * @param {string|number} weight
   * @param {ImmutableMap<string, ThemeFont>} fonts - list of fonts, defaults to theme fonts
   * @returns {boolean}
   */
  const isWeightInTheme = (fontName, weight, fonts = themeFonts) => {
    const selectedFont = fonts.filter((_, key) => key === `${fontName} ${weight}`);
    return !!selectedFont.size;
  };

  /**
   *
   * @param {GoogleFont} font
   * @param {ImmutableMap<string, ThemeFont>=} fonts
   * @returns
   */
  const isEntireFontInTheme = (font, fonts = themeFonts) => {
    const { family, variants } = font;
    return variants.reduce((result, variant) => {
      const { display } = GOOGLE_FONT_VARIANTS[variant];
      return !isWeightInTheme(family, display, fonts) ? false : result;
    }, true);
  };

  const _onResetFilters = () => {
    setSortBy("alpha");
    setCategory("all");
  };

  const _addFont = () => {
    if (font.name) {
      addFont(
        selectedWeights.map((weight) =>
          Immutable.Map({
            ...font,
            weight: weight.replace("italic", ""),
            name: `${font.family} ${GOOGLE_FONT_VARIANTS[weight].display}`,
            style: weight.includes("italic") ? "italic" : "normal",
          })
        )
      );

      resetPanel();
    }
  };

  const _onSearch = (e) => {
    setSearchQuery(e.target.value);
    setSearchingFonts(true);
  };

  const _onSelectWeight = (e, weight) => {
    if (e.target.checked) {
      setSelectedWeights([...selectedWeights, weight]);
    } else {
      setSelectedWeights(selectedWeights.filter((w) => w !== weight));
    }
  };

  const _onSearchFocus = (e) => {
    e.target.select();
    setSearchingFonts(true);
  };

  const { family } = font;

  return (
    <div className={sharedStyles["font-type-wrapper"]}>
      <div className={sharedStyles["font-selection-wrapper"]}>
        <Box>
          <Text variant="heading-1" variantStyle="strong">
            Google Fonts
          </Text>
        </Box>
        <Box mb="2">
          <Text variant="body-small">
            For extensive preview options, browse{" "}
            <a href="https://fonts.google.com/" rel="noreferrer" target="_blank">
              Google Fonts
            </a>
          </Text>
        </Box>

        <Box d="flex" flex="row" gap="2" align="center">
          <Select
            className={`${styles["google-sort-select"]} ${styles["google-font-select"]}`}
            longLabel
            label="Sort By"
            value={sortBy}
            onSISelect={_onSortByChange}
          >
            <SelectOption value="alpha">Alphabetical</SelectOption>
            <SelectOption value="popularity">Popularity</SelectOption>
            <SelectOption value="trending">Trending</SelectOption>
            <SelectOption value="date">New</SelectOption>
          </Select>
          <Select
            className={`${styles["google-font-select"]} ${styles["google-category-select"]}`}
            longLabel
            label="Category"
            value={category}
            onSISelect={_onCategoryChange}
          >
            <SelectOption value="all">All</SelectOption>
            <SelectOption value="serif">Serif</SelectOption>
            <SelectOption value="sans-serif">Sans-serif</SelectOption>
            <SelectOption value="display">Display</SelectOption>
            <SelectOption value="handwriting">Handwriting</SelectOption>
            <SelectOption value="monospace">Monospace</SelectOption>
          </Select>
          {(sortBy !== "alpha" || category !== "all") && (
            <Button variant="link" iconSize="20" icon-prefix="undo" onSIClick={_onResetFilters}>
              Reset
            </Button>
          )}
        </Box>

        <div className={styles["google-font-select-wrapper"]}>
          <Icon className={styles["google-font-select-icon"]} type="search" size="20" />
          <input
            type="text"
            className={styles["google-font-select-input"]}
            placeholder="Search Fonts"
            value={searchQuery}
            style={{
              borderBottomLeftRadius: searchingFonts ? "0" : "var(--radius-100)",
              borderBottomRightRadius: searchingFonts ? "0" : "var(--radius-100)",
              fontFamily: `${family}-lazy, sans-serif`,
            }}
            onFocus={_onSearchFocus}
            onChange={_onSearch}
          />
          {searchingFonts && (
            <div ref={searchListRef} className={styles["google-font-select-dropdown"]}>
              <div
                className={styles["google-font-select-dropdown-inner"]}
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualItem) => (
                  <GoogleFontPreview
                    styleHolder={styleHolder}
                    isEntireFontInTheme={isEntireFontInTheme}
                    key={googleFontList[virtualItem.index].family}
                    font={googleFontList[virtualItem.index]}
                    virtualRow={virtualItem}
                    onSelect={_onFamilyChange}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <Box mb="2" d="flex" className={styles["google-font-select-wrapper"]} justify="start">
          <When it={family}>
            <div style={{ display: "flex", flexDirection: "row", gap: "0.5rem", flexWrap: "wrap" }}>
              <Text variant="body-small" variantStyle="strong">
                Font Style
              </Text>
              <div>
                {weights.map((weight) => (
                  <Checkbox
                    key={weight}
                    value={weight}
                    disabled={isWeightInTheme(family, GOOGLE_FONT_VARIANTS[weight].display)}
                    onSIChange={(e) => _onSelectWeight(e, weight)}
                    checked={
                      selectedWeights.includes(weight) ||
                      isWeightInTheme(family, GOOGLE_FONT_VARIANTS[weight].display)
                    }
                    label={GOOGLE_FONT_VARIANTS[weight].display}
                  />
                ))}
              </div>
            </div>
          </When>
        </Box>
        <When it={family}>
          <Box d="flex" mb="2" justify="start">
            <Button
              variant="outline"
              data-e2e={uiPrefix("add-google-font-btn")}
              className={uiPrefix("add-google-font-btn")}
              onSIClick={_addFont}
            >
              Add Google Font
            </Button>
          </Box>
        </When>
      </div>
      <div className={sharedStyles["font-preview-wrapper"]}>
        {font.name && (
          <>
            <Text
              className={sharedStyles["font-preview-title"]}
              variant="body-small"
              variantStyle="strong"
            >
              Font Preview
            </Text>
            <div className={sharedStyles["font-preview-container"]}>
              {selectedWeights.map((selectedWeight) => (
                <FontPreview
                  justShowPreview
                  key={selectedWeight}
                  font={Immutable.fromJS({
                    ...font,
                    weight: selectedWeight.replace("italic", ""),
                    style: selectedWeight.includes("italic") ? "italic" : "normal",
                  })}
                  fontName={`${font.family} ${GOOGLE_FONT_VARIANTS[selectedWeight].display}`}
                  designContext={designContext}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FontPanelGoogle;
